import type { TvData, TvProgramme } from '@/types/tvTypes'

/** localStorage key for "include programmes with 'Signed' in title as BSL" (used on broadcaster upload screen). */
export const SIGNED_TITLE_PREFERENCE_KEY = 'tv_accessibility_include_signed_title'

/**
 * Returns true if the title indicates signed/BSL content (e.g. "Signed", "Signed: News", "SIGNED Gladiators").
 * Uses case-insensitive check for prefix "signed" followed by optional space or colon.
 */
export function isSignedTitle(title: string | null | undefined): boolean {
  if (title == null || typeof title !== 'string') return false
  const t = title.trim().toLowerCase()
  return t.startsWith('signed') && (t.length === 6 || t[6] === ' ' || t[6] === ':')
}

/**
 * Strip "Signed: " or "Signed " prefix (case-insensitive) for matching overlay titles to main listings.
 * e.g. "Signed: The Night Manager" → "The Night Manager". Use when building overlay keys so accessibility
 * records with "Signed: X" match programmes titled "X" in the main feed.
 */
export function stripSignedPrefix(title: string | null | undefined): string {
  if (title == null || typeof title !== 'string') return ''
  const t = title.trim()
  const lower = t.toLowerCase()
  if (!lower.startsWith('signed')) return t
  let i = 6 // length of 'signed'
  while (i < t.length && (t[i] === ':' || t[i] === ' ')) i++
  return t.slice(i).trim()
}

/**
 * Scans programmes for titles prefixed with "Signed" and sets has_bsl = true on those.
 * Mutates data in place and returns the number of programmes marked (for display/merge feedback).
 */
export function applySignedTitleDetection(data: TvData): { count: number; programmes: TvProgramme[] } {
  const marked: TvProgramme[] = []
  for (const p of data.programmes) {
    if (isSignedTitle(p.title)) {
      p.has_bsl = true
      marked.push(p)
    }
  }
  return { count: marked.length, programmes: marked }
}
